/**
 * `GET /api/cart` — buyer cart with enriched listing + seller capability data.
 *
 * Response is an array (possibly empty) of cart lines. Each line carries
 * cart-row identity (id, quantity, shipping_method_id) and a deeply nested
 * `listings` block that combines listing columns, derived seller fields,
 * and resolved shipping methods.
 *
 * # Naming history (resolved 2026-06-07 in PR #253)
 *
 * The capability array was emitted under both names across endpoints:
 * `paypalCapabilities` (here, in cart) and `sellerCapabilities` (in
 * `listing-meta`). PR #253 unified both to `paypalCapabilities`. This
 * schema pins that decision.
 *
 * # Why fewer fields than the route actually emits
 *
 * The route spreads ALL listing columns via `{ ...L, ...overrides }` — that's
 * an implementation choice driven by the Supabase select query, not a wire
 * contract. We pin ONLY the fields that consumers (web `cart-provider`/
 * `cart-content`/`shipping-config`/9 surfaces and `apps/mobile/lib/api-kraahl.ts`
 * `CartApiLine`) actually READ.
 *
 * ⚠️ Zod v4 REALITY CHECK (2026-07-04): `z.object` STRIPS unknown keys on
 * parse — unpinned fields do NOT pass through. Every consumer reads the
 * PARSED value, so "read but unpinned" = the field is silently `undefined`
 * forever. This killed the is_unlisted gates on both clients AND web's
 * preorder consent gate before the 2026-07-04 pins. The rule is absolute:
 * a field any client reads MUST be pinned here, in the same PR.
 *
 * If a new client starts depending on an additional field, ADD it here in
 * the same PR. The vendored mobile `CartApiLine` is the source-of-truth
 * test bed (any field present there must be present here).
 *
 * # Shippo wire surface (WS4.4, 2026-06-18)
 *
 * `shipping_mode` + `parcel_*` are pinned here because web's `cart-provider`
 * threads them into `CartItemData` so `deriveShippingMode` /
 * `aggregateParcel` (lib/checkout/*) can flip the seller's PayPalCheckout
 * into the WS4.3c live-rate sub-flow. Without this pin, the field is just
 * "extra Zod passthrough" and TypeScript can't see it on the consumer side.
 *
 * `carrier_provider` is NOT pinned yet — the live-rate sub-flow always
 * assumes Shippo today and `CarrierProvider` is a single-value union
 * (`lib/shipping/types.ts`). Left-fix audit lifts it the moment a second
 * provider lands (PLAN §K.6). Mobile (`apps/mobile/lib/api-kraahl.ts
 * CartApiLine`) does not consume these fields today — additive contract
 * change, no mobile sync required.
 *
 * # Mobile dual-name tolerance
 *
 * Mobile parses `shippingMethods` OR `shipping_methods`, `selectedShippingMethod`
 * OR `selected_shipping_method`. Today the route emits ONLY the camelCase
 * forms; the mobile snake-case fallback exists as historical drift insurance
 * and is dormant. We pin camelCase here — flipping that back is a deliberate
 * coordinated change, not silent drift.
 *
 * @see app/api/cart/route.ts — handler (single-source-of-truth emitter)
 * @see apps/mobile/lib/api-kraahl.ts CartApiLine — mobile vendored consumer type
 * @see .tondocs/payments/API_RESPONSE_AUDIT.md §2 — High-tier surface
 */

import { z } from "zod";
import { pinned } from "./pin";
import { rawImageRefField, type RawImageRef } from "./raw-image-ref";
import {
  PaymentProviderName,
  PaymentProviderNameSchema,
} from "./payments-seller-provider";
import { ShippingMethod, ShippingMethodSchema } from "./shipping-method";

/**
 * Listing fields the cart route surfaces — UNION of what every cart consumer
 * (web + mobile) reads + every load-bearing money/identity field. Listing
 * columns spread by the route's `...L` that no client currently reads are
 * intentionally absent: they pass through Zod parse but don't enter the type
 * surface clients bind to.
 *
 * `currency` is the server-resolved uppercase form (`resolvedCurrency`), never
 * null after enrichment. `image_urls` is `unknown` — the column is a JSON
 * blob the cart surface treats as opaque (renderers narrow it themselves).
 */
export interface CartListingNested {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  /** Server-resolved uppercase ISO-4217. Never null in the response. */
  currency: string;
  price: number;
  /** Stock — total quantity available on the listing. */
  quantity: number;
  condition: string | null;
  grade: string | null;
  /** Opaque JSON — clients narrow at use site. */
  image_urls: unknown;
  /** ISO timestamp string, or null. Surfaces chat-private lifecycle to consumers. */
  chat_private_cancelled_at: string | null;
  /**
   * Unlisted (soft-hidden) listing flag. Optional on the wire for emitter
   * back-compat, but MUST stay pinned here: Zod v4 `z.object` strips unknown
   * keys at the boundary parse, so an unpinned field silently disappears —
   * both clients' unlisted availability gates were dead code until this pin
   * (parity audit 2026-07-04).
   */
  is_unlisted?: boolean | null;
  /**
   * Preorder axis — same Zod-v4 strip class as `is_unlisted` above: the cart
   * route has always emitted these (SELECT app/api/cart/route.ts) and
   * cart-provider reads them for the PREORDER CONSENT GATE
   * (.tondocs/listings/PREORDER.md), but they were unpinned → stripped →
   * `checkoutPreorderItems` was always empty and the consent gate dead on
   * web, absent on mobile. Pinned 2026-07-04 (parity audit).
   */
  type?: string | null;
  preorder_deadline?: string | null;
  estimated_delivery?: string | null;
  preorder_status?: string | null;
  min_orders?: number | null;
  max_orders?: number | null;
  /** Derived from `profiles.username`, defaulted to "Unknown". */
  sellerUsername: string;
  /**
   * `profiles.avatar_url` as STORED (bucket-relative since phase C). Branded
   * so it cannot reach `next/image` / RN `source` without `resolveStorageUrl`
   * — this exact field crashed the cart drawer via the alias chain
   * `avatar_url` -> `sellerAvatarUrl` -> `profileImage` (fix 42862ded).
   * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §9
   */
  sellerAvatarUrl: RawImageRef | null;
  /** Trimmed; null when blank. */
  sellerCheckoutNote: string | null;
  /** True when seller is banned — UI surfaces a warning and blocks checkout. */
  sellerBanned: boolean;
  shippingMethods: ShippingMethod[];
  /** Resolved against `cart_items.shipping_method_id`. Null when none picked. */
  selectedShippingMethod: ShippingMethod | null;
  /**
   * Shippo white-label discriminator (WS4.4). Drives `deriveShippingMode` on
   * the checkout side: `'live_rate'` opens the address-first sub-flow,
   * `'legacy'` / null preserves the flat-rate path. DB CHECK constraint
   * enforces these two values exactly (renamed from `'shippo'` →
   * `'live_rate'` in `20260617120000_provider_neutral_shipping.sql`,
   * PLAN §K.6, S3).
   */
  shipping_mode: "legacy" | "live_rate" | null;
  /**
   * Required when `shipping_mode === 'live_rate'`. Aggregated by
   * `aggregateParcel`. Post-K.6 (PLAN §A "Parcel units" policy C,
   * 2026-06-22) storage is canonical grams + mm; the wire shape keeps the
   * legacy field NAMES (`parcel_weight` not `parcel_weight_grams`) so
   * downstream consumers (cart-provider, aggregate-parcel) don't break,
   * but the VALUES are canonical and the unit fields are now pinned to the
   * `'g'` / `'mm'` literals (slice C2, 2026-06-23 — legacy
   * `lb`/`oz`/`kg`/`in`/`cm` variants gone).
   */
  parcel_weight: number | null;
  parcel_length: number | null;
  parcel_width: number | null;
  parcel_height: number | null;
  parcel_mass_unit: "g" | null;
  parcel_distance_unit: "mm" | null;
}

export interface CartLine {
  /** `cart_items.id`. */
  id: string;
  /** `cart_items.quantity` — buyer-chosen, capped at listing stock server-side. */
  quantity: number;
  /** ISO timestamp of last cart-row update. */
  updated_at: string;
  /** `cart_items.listing_id`. */
  listing_id: string;
  /** `cart_items.shipping_method_id`, or null when buyer hasn't picked. */
  shipping_method_id: string | null;
  paypalMerchantId: string | null;
  /** PayPal merchant integrations (`CUSTOM_CARD_PROCESSING`, etc.). */
  paypalCapabilities: string[];
  availableProviders: PaymentProviderName[];
  listings: CartListingNested;
}

/** Full route response — possibly empty. */
export type CartResponse = CartLine[];

export const CartListingNestedSchema = pinned<CartListingNested>()(
  z.object({
    id: z.string(),
    user_id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    currency: z.string(),
    price: z.number(),
    quantity: z.number(),
    condition: z.string().nullable(),
    grade: z.string().nullable(),
    image_urls: z.unknown(),
    chat_private_cancelled_at: z.string().nullable(),
    is_unlisted: z.boolean().nullable().optional(),
    type: z.string().nullable().optional(),
    preorder_deadline: z.string().nullable().optional(),
    estimated_delivery: z.string().nullable().optional(),
    preorder_status: z.string().nullable().optional(),
    min_orders: z.number().nullable().optional(),
    max_orders: z.number().nullable().optional(),
    sellerUsername: z.string(),
    sellerAvatarUrl: rawImageRefField().nullable(),
    sellerCheckoutNote: z.string().nullable(),
    sellerBanned: z.boolean(),
    shippingMethods: z.array(ShippingMethodSchema),
    selectedShippingMethod: ShippingMethodSchema.nullable(),
    shipping_mode: z.enum(["legacy", "live_rate"]).nullable(),
    parcel_weight: z.number().nullable(),
    parcel_length: z.number().nullable(),
    parcel_width: z.number().nullable(),
    parcel_height: z.number().nullable(),
    parcel_mass_unit: z.literal("g").nullable(),
    parcel_distance_unit: z.literal("mm").nullable(),
  }),
);

export const CartLineSchema = pinned<CartLine>()(
  z.object({
    id: z.string(),
    quantity: z.number(),
    updated_at: z.string(),
    listing_id: z.string(),
    shipping_method_id: z.string().nullable(),
    paypalMerchantId: z.string().nullable(),
    paypalCapabilities: z.array(z.string()),
    availableProviders: z.array(PaymentProviderNameSchema),
    listings: CartListingNestedSchema,
  }),
);

export const CartResponseSchema = pinned<CartResponse>()(
  z.array(CartLineSchema),
);
